import type { Locale } from "@/i18n/locales";

function copy<T>(nl: T, en: T, de: T, fr: T, es: T): Record<Locale, T> {
  return { nl, en, de, fr, es };
}

type Benefit = {
  icon: "map" | "scan" | "bell" | "users" | "shield" | "file";
  title: string;
  description: string;
};

type Step = {
  number: string;
  title: string;
  description: string;
};

type LandingPageCopy = {
  meta: {
    title: string;
    description: string;
  };
  eyebrow: string;
  title: string;
  intro: string;
  proof: string;
  benefitsHeading: string;
  benefits: Benefit[];
  stepsHeading: string;
  steps: Step[];
  ctaTitle: string;
  ctaText: string;
};

type LandingMagnetsCopy = {
  travelScan: LandingPageCopy;
  boosterReminders: LandingPageCopy;
  vaccinationChecklist: LandingPageCopy;
};

const nl: LandingMagnetsCopy = {
  travelScan: {
    meta: {
      title: "Gratis reisvaccinatie-scan | Vaxy",
      description:
        "Check in 2 minuten welke reisvaccins je nodig hebt. Krijg een persoonlijk overzicht en bewaar het in Vaxy.",
    },
    eyebrow: "Reisvaccins",
    title: "Check in 2 minuten welke reisvaccins je nodig hebt",
    intro:
      "Vul je bestemming in en krijg meteen een persoonlijk overzicht. Sla het daarna op in Vaxy zodat je nooit meer hoeft te zoeken.",
    proof: "Gebaseerd op LCR-richtlijnen en handig voor gezinnen en reizigers.",
    benefitsHeading: "Waarom deze scan werkt",
    benefits: [
      {
        icon: "map",
        title: "Direct per bestemming",
        description:
          "Je ziet meteen welke vaccins relevant zijn voor je reis, zonder zelf te hoeven zoeken in lange lijsten.",
      },
      {
        icon: "scan",
        title: "Makkelijk te bewaren",
        description:
          "Na de scan is het logisch om het resultaat in Vaxy op te slaan, samen met je andere vaccinaties.",
      },
      {
        icon: "shield",
        title: "Minder last-minute stress",
        description:
          "Wie vroeg weet wat nodig is, kan op tijd een afspraak plannen en mist minder vaak een prik.",
      },
    ],
    stepsHeading: "Hoe de landingspagina werkt",
    steps: [
      {
        number: "01",
        title: "Kies je bestemming",
        description:
          "De bezoeker vult land, vertrekdatum of reisduur in en krijgt direct context.",
      },
      {
        number: "02",
        title: "Bekijk het advies",
        description:
          "Toon een kort en duidelijk overzicht van de mogelijke reisvaccins en vervolgstappen.",
      },
      {
        number: "03",
        title: "Download Vaxy",
        description:
          "De scan eindigt met een duidelijke CTA om de app te installeren en het overzicht veilig te bewaren.",
      },
    ],
    ctaTitle: "Download Vaxy gratis",
    ctaText:
      "Bewaar reisvaccins, herinneringen en overzichten in één app, zodat je altijd voorbereid op pad gaat.",
  },
  boosterReminders: {
    meta: {
      title: "Gratis booster reminder | Vaxy",
      description:
        "Mis nooit meer een booster of herhaalprik. Stel gratis herinneringen in en bewaar alles veilig in Vaxy.",
    },
    eyebrow: "Herhaalprikken",
    title: "Mis nooit meer een booster of herhaalprik",
    intro:
      "Krijg een simpele reminder voor griep, gordelroos, pneumokokken en andere prikken die je niet wilt vergeten.",
    proof: "Ideaal voor iedereen die rust wil in plaats van alles zelf onthouden.",
    benefitsHeading: "Waarom dit overtuigt",
    benefits: [
      {
        icon: "bell",
        title: "Altijd op tijd",
        description:
          "Een reminder voorkomt dat mensen een prik uitstellen of vergeten wanneer het druk is.",
      },
      {
        icon: "users",
        title: "Voor het hele gezin",
        description:
          "Je kunt herinneringen logisch koppelen aan meerdere gezinsleden of profielen.",
      },
      {
        icon: "shield",
        title: "Bescherming blijft actueel",
        description:
          "De app maakt het makkelijker om je bescherming niet stilletjes te laten verlopen.",
      },
    ],
    stepsHeading: "Hoe de landingspagina werkt",
    steps: [
      {
        number: "01",
        title: "Kies de booster",
        description:
          "De bezoeker kiest welke prik relevant is, bijvoorbeeld griep of een herhaalprik.",
      },
      {
        number: "02",
        title: "Zet de reminder aan",
        description:
          "Laat de gebruiker weten dat de reminder veilig in de app kan worden opgeslagen.",
      },
      {
        number: "03",
        title: "Installeer de app",
        description:
          "Sluit af met een duidelijke installatie-actie en een korte uitleg van de waarde.",
      },
    ],
    ctaTitle: "Download Vaxy gratis",
    ctaText:
      "Stel herinneringen in, houd boosters bij en ontvang een seintje zodra iets opnieuw nodig is.",
  },
  vaccinationChecklist: {
    meta: {
      title: "Gratis vaccinatie-checklist | Vaxy",
      description:
        "Download een gratis vaccinatie-checklist voor gezin, reis of dagelijkse zorg en bewaar hem in Vaxy.",
    },
    eyebrow: "Checklists",
    title: "Check of jouw vaccinaties nog kloppen",
    intro:
      "Download een gratis checklist voor kinderen, reizigers of volwassenen en zie in één oogopslag wat nog openstaat.",
    proof: "Goed voor ouders, reizigers en iedereen die overzicht wil zonder gedoe.",
    benefitsHeading: "Waarom deze checklist werkt",
    benefits: [
      {
        icon: "file",
        title: "Snel te begrijpen",
        description:
          "De bezoeker hoeft geen lange uitleg te lezen, maar krijgt direct een heldere checklist.",
      },
      {
        icon: "users",
        title: "Geschikt voor meerdere profielen",
        description:
          "Je kunt de checklist laten aansluiten op ouders, kinderen of individuele reizigers.",
      },
      {
        icon: "scan",
        title: "Natuurlijke stap naar de app",
        description:
          "Na de checklist is het logisch om alles digitaal op te slaan en later te blijven bijhouden.",
      },
    ],
    stepsHeading: "Hoe de landingspagina werkt",
    steps: [
      {
        number: "01",
        title: "Kies het profiel",
        description:
          "Laat de bezoeker kiezen tussen gezin, reis of persoonlijke vaccinatiecheck.",
      },
      {
        number: "02",
        title: "Bekijk de checklist",
        description:
          "Toon de checklist compact, visueel en zonder onnodige afleiding.",
      },
      {
        number: "03",
        title: "Open Vaxy",
        description:
          "Sluit af met de app-installatie zodat de checklist niet op zichzelf blijft staan.",
      },
    ],
    ctaTitle: "Download Vaxy gratis",
    ctaText:
      "Bewaar checklist, vaccinatieoverzicht en herinneringen op één plek en houd alles eenvoudig bij.",
  },
};

const en: LandingMagnetsCopy = {
  travelScan: {
    meta: {
      title: "Free travel vaccine scan | Vaxy",
      description:
        "Check which travel vaccines you need in 2 minutes. Get a personal overview and save it in Vaxy.",
    },
    eyebrow: "Travel vaccines",
    title: "Check which travel vaccines you need in 2 minutes",
    intro:
      "Enter your destination and get a personal overview right away. Save it in Vaxy so you never have to search again.",
    proof: "Based on LCR guidance and useful for families and travellers.",
    benefitsHeading: "Why this scan converts",
    benefits: [
      {
        icon: "map",
        title: "Instant by destination",
        description:
          "Users immediately see which vaccines are relevant for their trip, without digging through long lists.",
      },
      {
        icon: "scan",
        title: "Easy to keep",
        description:
          "After the scan, it feels natural to store the result in Vaxy with the rest of the vaccination record.",
      },
      {
        icon: "shield",
        title: "Less last-minute stress",
        description:
          "People who know what they need early can book on time and miss fewer vaccines.",
      },
    ],
    stepsHeading: "How the landing page works",
    steps: [
      {
        number: "01",
        title: "Choose your destination",
        description:
          "The visitor enters country, departure date or trip length and gets context immediately.",
      },
      {
        number: "02",
        title: "See the advice",
        description:
          "Show a short, clear overview of relevant vaccines and next steps.",
      },
      {
        number: "03",
        title: "Download Vaxy",
        description:
          "End with a clear install CTA so the result can be saved securely in the app.",
      },
    ],
    ctaTitle: "Download Vaxy for free",
    ctaText:
      "Store travel vaccines, reminders and overviews in one app so you can leave prepared.",
  },
  boosterReminders: {
    meta: {
      title: "Free booster reminder | Vaxy",
      description:
        "Never miss a booster or repeat vaccination again. Set free reminders and store everything safely in Vaxy.",
    },
    eyebrow: "Repeat vaccines",
    title: "Never miss a booster or repeat vaccination again",
    intro:
      "Get a simple reminder for flu, shingles, pneumococcal shots and other vaccinations you do not want to forget.",
    proof: "Perfect for anyone who wants peace of mind instead of keeping track manually.",
    benefitsHeading: "Why this works",
    benefits: [
      {
        icon: "bell",
        title: "Always on time",
        description:
          "A reminder prevents people from postponing or forgetting a shot when life gets busy.",
      },
      {
        icon: "users",
        title: "For the whole family",
        description:
          "Reminders can be tied neatly to multiple family members or profiles.",
      },
      {
        icon: "shield",
        title: "Protection stays current",
        description:
          "The app makes it easier to avoid quietly letting protection expire.",
      },
    ],
    stepsHeading: "How the landing page works",
    steps: [
      {
        number: "01",
        title: "Pick the booster",
        description:
          "The visitor chooses the relevant vaccine, such as flu or a repeat shot.",
      },
      {
        number: "02",
        title: "Turn on the reminder",
        description:
          "Show that the reminder can be stored safely in the app.",
      },
      {
        number: "03",
        title: "Install the app",
        description:
          "End with a clear install action and a short explanation of the benefit.",
      },
    ],
    ctaTitle: "Download Vaxy for free",
    ctaText:
      "Set reminders, track boosters and get nudged as soon as something is due again.",
  },
  vaccinationChecklist: {
    meta: {
      title: "Free vaccination checklist | Vaxy",
      description:
        "Download a free vaccination checklist for family, travel or everyday care and keep it in Vaxy.",
    },
    eyebrow: "Checklists",
    title: "Check whether your vaccinations are still up to date",
    intro:
      "Download a free checklist for children, travellers or adults and see at a glance what is still open.",
    proof: "Good for parents, travellers and anyone who wants clarity without the hassle.",
    benefitsHeading: "Why this checklist converts",
    benefits: [
      {
        icon: "file",
        title: "Quick to understand",
        description:
          "The visitor gets a clear checklist right away instead of a long explanation.",
      },
      {
        icon: "users",
        title: "Works for multiple profiles",
        description:
          "You can tailor the checklist for parents, children or individual travellers.",
      },
      {
        icon: "scan",
        title: "Natural path to the app",
        description:
          "After the checklist, it makes sense to store everything digitally and keep tracking it.",
      },
    ],
    stepsHeading: "How the landing page works",
    steps: [
      {
        number: "01",
        title: "Choose a profile",
        description:
          "Let the visitor choose between family, travel or personal vaccination check.",
      },
      {
        number: "02",
        title: "Review the checklist",
        description:
          "Show the checklist in a compact, visual way without unnecessary friction.",
      },
      {
        number: "03",
        title: "Open Vaxy",
        description:
          "Close with the app install so the checklist does not stand on its own.",
      },
    ],
    ctaTitle: "Download Vaxy for free",
    ctaText:
      "Keep your checklist, vaccination overview and reminders in one place and stay on track.",
  },
};

export type LandingMagnets = typeof nl;

const de: LandingMagnets = en;
const fr: LandingMagnets = en;
const es: LandingMagnets = en;

export const landingMagnets: Record<Locale, LandingMagnets> = copy(
  nl,
  en,
  de,
  fr,
  es,
);

export type LandingMagnetKey = keyof LandingMagnets;
